import { Users } from 'lucide-react';
import { GameTimeDisplay } from '@/components/game/GameTimeDisplay';

interface PartyMemberSummary {
  id: string;
  name: string;
  avatarEmoji?: string;
  avatarUrl?: string;
  class?: string;
  level?: number;
}

interface PartyData {
  id: string;
  name: string;
  partyImageUrl?: string;
  memberCharacterIds: string[];
  members?: PartyMemberSummary[];
  leaderCharacterId?: string;
  selectedModuleId?: string;
  selectedModuleName?: string;
  location?: string;
  status?: string;
  lastSessionEnd?: {
    finalGameTimeSeconds?: number;
  };
}

interface PartyCardProps {
  party: PartyData;
}

export default function PartyCard({ party }: PartyCardProps) {
  const isInGame = party.location === 'game';
  const members = party.members ?? [];

  return (
    <div className="rounded-xl border bg-card/40 text-card-foreground shadow-lg backdrop-blur-md p-5">
      <div className="relative mb-3">
        <div
          className={`flex items-center justify-center gap-2 min-w-0 ${isInGame ? 'px-20' : ''}`}
        >
          <Users className="h-5 w-5 shrink-0 text-primary" />
          <h3 className="truncate font-bold text-foreground">{party.name || 'Unnamed Party'}</h3>
        </div>
        {isInGame && (
          <span className="absolute right-0 top-1/2 -translate-y-1/2 inline-flex items-center rounded-full bg-green-500/15 px-2 py-0.5 text-xs font-medium text-green-400">
            In Game
          </span>
        )}
      </div>

      {/* Latest party hero image, between the title and the roster */}
      {party.partyImageUrl && (
        <img
          src={party.partyImageUrl}
          alt={`${party.name || 'Party'} hero image`}
          className="mb-3 w-full rounded-lg object-cover max-h-40 ring-1 ring-border"
        />
      )}

      {/* Party members */}
      {members.length > 0 ? (
        <div className="flex gap-4 justify-center">
          {members.map((member) => (
            <div key={member.id} className="flex flex-col items-center gap-1.5 min-w-0">
              {member.avatarUrl ? (
                <img
                  src={member.avatarUrl}
                  alt={member.name}
                  className="h-14 w-14 rounded-full object-cover ring-2 ring-border"
                />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted ring-2 ring-border text-xl">
                  {member.avatarEmoji || member.name.charAt(0)}
                </div>
              )}
              <p className="text-sm font-medium text-foreground truncate max-w-[80px] text-center font-character">
                {member.name}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>Members</span>
            <span className="text-foreground">{party.memberCharacterIds?.length || 0}</span>
          </div>
        </div>
      )}

      {/* Module / Status info */}
      <div className="mt-3 space-y-2 text-sm text-muted-foreground">
        {party.selectedModuleId && (
          <p className="text-center font-bold text-foreground">
            {party.selectedModuleName ?? party.selectedModuleId}
          </p>
        )}
        {party.lastSessionEnd?.finalGameTimeSeconds != null && (
          <div className="flex justify-center">
            <GameTimeDisplay seconds={party.lastSessionEnd.finalGameTimeSeconds} />
          </div>
        )}
        {party.status && (
          <div className="flex justify-between">
            <span>Status</span>
            <span className="text-foreground capitalize">{party.status}</span>
          </div>
        )}
      </div>
    </div>
  );
}
